import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import {
	Box,
	FormControl,
	FormLabel,
	IconButton,
	Option,
	Select,
	Typography
} from '@mui/joy';

interface DatatableFooterProps<T extends Record<string, string | number>> {
	rows: T[];
	page: number;
	rowsPerPage: number;
	handleChangeRowsPerPage: (newValue: number | null) => void;
	handleChangePage: (newPage: number) => void;
}

function labelDisplayedRows({
	from,
	to,
	count
}: {
	from: number;
	to: number;
	count: number;
}) {
	return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

export const DatatableFooter = <T extends Record<string, string | number>>({
	rows,
	page,
	rowsPerPage,
	handleChangeRowsPerPage,
	handleChangePage
}: DatatableFooterProps<T>) => {
	const getLabelDisplayedRowsTo = () => {
		if (rows.length === -1) {
			return (page + 1) * rowsPerPage;
		}
		return rowsPerPage === -1
			? rows.length
			: Math.min(rows.length, (page + 1) * rowsPerPage);
	};

	return (
		<Box
			sx={{
				position: 'sticky',
				bottom: 0,
				right: 0,
				backgroundColor: 'white', // Adjust as needed
				borderTop: '1px solid var(--TableCell-borderColor)',
				display: 'flex',
				justifyContent: 'flex-start',
				padding: '8px', // Adjust as needed'
				paddingRight: { xs: '50px', md: '150px', lg: '250px' }
			}}
		>
			<Box
				sx={{
					maxWidth: '1039px',
					display: 'flex',
					justifyContent: { xs: 'center', md: 'flex-end' },
					flexDirection: { xs: 'column', md: 'row' },
					flexGrow: 1,
					gap: { xs: 2, md: 0 },
					alignItems: { xs: 'flex-end', md: 'center' }
				}}
			>
				<FormControl
					orientation='horizontal'
					size='sm'
					aria-label='Select items per page'
				>
					<FormLabel
						sx={{ fontSize: '0.875rem' }}
						id='items-per-page-select-label'
						htmlFor='items-per-page-button'
					>
						Items per page:
					</FormLabel>
					<Select
						sx={{ borderRadius: 0 }}
						onChange={(_, value) => handleChangeRowsPerPage(value)}
						value={rowsPerPage}
						slotProps={{
							button: {
								id: 'itemsPerPageSelectButton',
								'aria-labelledby':
									'itemsPerPageSelectLabel itemsPerPageSelectButton'
							}
						}}
					>
						<Option value={10} aria-label='10'>
							10
						</Option>
						<Option value={25} aria-label='25'>
							25
						</Option>
						<Option value={50} aria-label='50'>
							50
						</Option>
						<Option value={100} aria-label='100'>
							100
						</Option>
					</Select>
				</FormControl>
				<Box sx={{ display: 'flex' }}>
					<Typography
						textAlign='center'
						sx={{ marginLeft: '43px', minWidth: 80, fontSize: '0.875rem' }}
					>
						{labelDisplayedRows({
							from: rows.length === 0 ? 0 : page * rowsPerPage + 1,
							to: getLabelDisplayedRowsTo(),
							count: rows.length === -1 ? -1 : rows.length
						})}
					</Typography>
					<Box sx={{ display: 'flex', gap: 0, marginLeft: '16px' }}>
						<IconButton
							aria-label='Previous page'
							size='sm'
							color='neutral'
							variant='outlined'
							disabled={page === 0}
							onClick={() => handleChangePage(page - 1)}
							sx={{ bgcolor: 'background.surface', borderRadius: 0 }}
						>
							<KeyboardArrowLeft />
						</IconButton>
						<IconButton
							aria-label='Next page'
							size='sm'
							color='neutral'
							variant='outlined'
							disabled={
								rows.length !== -1
									? page >= Math.ceil(rows.length / rowsPerPage) - 1
									: false
							}
							onClick={() => handleChangePage(page + 1)}
							sx={{
								bgcolor: 'background.surface',
								borderRadius: 0,
								fontSize: '1rem'
							}}
						>
							<KeyboardArrowRight />
						</IconButton>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
